import type { BadgeProps } from '@nuxt/ui'
import { isDocVersion, CURRENT_DOCS_VERSION, DOCS_REFS, docsPathPrefix, type DocVersion } from '#shared/utils/docs'
import { docsInstanceKey, type ContentInstanceKey } from '#shared/utils/content'

interface Version {
  label: string
  shortTag: 'v5' | 'v4' | 'v3' | 'v2'
  branch: string
  tagColor: BadgeProps['color']
  path: string
  /** `unsupported` versions are end of life: no bug fixes, no security patches. */
  status?: 'prerelease' | 'stable' | 'unsupported'
  /** Date the version reached end of life, for `unsupported` versions. */
  endOfLife?: string
  /** Third-party extended support offering, for `unsupported` versions. */
  extendedSupport?: string
}

const versions: Version[] = [
  {
    label: 'Version 5',
    shortTag: 'v5',
    branch: DOCS_REFS['5.x'].branch,
    tagColor: 'warning',
    path: docsPathPrefix('5.x'),
    status: 'prerelease'
  },
  {
    label: 'Version 4',
    shortTag: 'v4',
    branch: DOCS_REFS['4.x'].branch,
    tagColor: 'primary',
    path: docsPathPrefix('4.x'),
    status: 'stable'
  },
  {
    label: 'Version 3',
    shortTag: 'v3',
    branch: DOCS_REFS['3.x'].branch,
    tagColor: 'neutral',
    path: docsPathPrefix('3.x'),
    status: 'unsupported',
    endOfLife: '31 July 2026',
    extendedSupport: 'https://www.herodevs.com/support/nuxt-nes?utm_source=nuxtjs&utm_medium=affiliate&utm_campaign=nuxt3eol&utm_content=link'
  },
  {
    label: 'Version 2',
    shortTag: 'v2',
    branch: '2.x',
    tagColor: 'neutral',
    path: 'https://v2.nuxt.com',
    status: 'unsupported',
    endOfLife: '30 June 2024'
  }
]

const tagMap: Record<Version['shortTag'], string> = {
  v5: '5x',
  v4: '4x',
  v3: '3x',
  v2: '2x'
}

export const useDocsTags = () => {
  const { data: tags } = useAsyncData('versions', async () => {
    const { 'dist-tags': distTags } = await $fetch<{ 'dist-tags': Record<string, string> }>('https://registry.npmjs.org/nuxt')
    return Object.fromEntries(
      Object.entries(tagMap).map(([shortTag]: [keyof typeof tagMap, string]) => {
        // TODO: remove nightly fallback when Nuxt 5 is released
        if (shortTag === 'v5') return [shortTag, distTags['5x'] ?? '5 (nightly)']
        return [shortTag, distTags[tagMap[shortTag]] ?? distTags.latest]
      })
    )
  }, { default: () => ({}) })

  return { tags }
}

export const useDocsVersion = () => {
  const route = useRoute()
  const { track } = useAnalytics()

  const version = computed(() => {
    if (route.path.startsWith(docsPathPrefix('5.x'))) {
      return versions.find(v => v.path === docsPathPrefix('5.x'))
    }

    if (route.path.startsWith(docsPathPrefix('3.x'))) {
      return versions.find(v => v.path === docsPathPrefix('3.x'))
    }

    // Default to v4 (current stable)
    return versions.find(v => v.path === docsPathPrefix('4.x'))
  })

  const items = computed(() => versions.map(v => ({
    ...v,
    label: v.status === 'unsupported' ? `${v.label} (EOL)` : v.label,
    ...(v.branch === version.value.branch
      ? {
          checked: true,
          color: v.tagColor,
          type: 'checkbox' as const
        }
      : {
          to: v.path === 'https://v2.nuxt.com' ? v.path : route.path.replace(version.value.path, v.path),
          onSelect: () => track('Version Switched', { version: v.shortTag })
        })
  })))

  /** The active docs version, derived from `path`. */
  const docsVersion = computed<DocVersion>(() => {
    const segment = version.value.path.replace('/docs/', '')
    return isDocVersion(segment) ? segment : CURRENT_DOCS_VERSION
  })

  /** The content instance key holding the active version's docs. */
  const instanceKey = computed<ContentInstanceKey>(() => docsInstanceKey(docsVersion.value))

  return {
    items,
    version,
    versions,
    docsVersion,
    instanceKey
  }
}
